import { type VariantProps, cva } from "class-variance-authority";
import clsx from "clsx";
import { FieldError, TextField } from "react-aria-components";
import type { ControllerRenderProps } from "react-hook-form";

import { uiOutlineClass } from "@/components/ui/global/outline";
import { InputHeader, type InputHeaderProps } from "@/components/ui/inputs/shared/InputHeader";
import { Typography } from "@/components/ui/text/Typography/Typography";

export const textInput = cva(
  [
    "w-full",
    "rounded-input-rounding",
    "bg-elevation-background",
    "px-input-side-spacing",
    "font-primary",
    "font-labels-default",
    "text-text-default-primary",
    uiOutlineClass,
    "focus-visible:!outline-none",
    "invalid:border invalid:border-info-error-outline",
    "focus:border focus:border-interactive-primary-idle",
    "focus-within:border focus-within:border-interactive-primary-idle",
    "focus-visible:outline-interactive-primary-focus",
    "hover:border hover:border-interactive-primary-hover",
    "!outline-none focus-within:!outline-none",
    "dm:text-mobile-body-2 t:text-desktop-body-3",
  ],
  {
    variants: {
      variant: {
        outlined: ["border border-elevation-outline-2", "py-height-s"],
        filled: [
          "border border-transparent bg-elevation-surface-2 focus:bg-elevation-surface-2",
          "py-height-s",
          "outline-none",
          "focus:border-elevation-outline-2",
          "hover:border-transparent",
          "rounded-xs",
        ],
        inline: ["border border-transparent", "py-height-xs"],
        chat: [
          "transition-colors duration-150",
          "border-none",
          "!outline-none",
          "focus:border-none",
          "focus-visible:!outline-none",
          "h-auto",
          "hover:bg-elevation-surface-2",
          "bg-elevation-surface-2",
          "active:bg-elevation-surface-1",
          "resize-none",
          "min-h-10",
          "max-h-20",
          "placeholder:text-label-4",
        ],
        login: [
          "!text-elevation-background",
          "bg-interactive-primary-idle",
          "border",
          "border-elevation-background",
          "py-height-s",
          "placeholder:text-interactive-primary-disabled",
          "focus:!border focus:border-interactive-secondary-disabled",
          "hover:border hover:border-interactive-secondary-disabled",
          "invalid:border invalid:border-info-error-outline",
        ],
        search: [
          "!rounded-full border border-elevation-outline-2 bg-elevation-surface-1 hover:border-elevation-outline-3 focus:border-elevation-outline-3",
          "py-height-s",
        ],
      },
    },
    defaultVariants: {
      variant: "outlined",
    },
  },
);

export type TextInputVariantProps = VariantProps<typeof textInput>;

export type TextInputWrapperProps = InputHeaderProps & {
  loginLabel?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
  textfieldClassName?: string;
  field?: Omit<ControllerRenderProps, "ref">;
};

export const TextInputWrapper = ({
  label,
  tooltipText,
  helperText,
  required,
  error,
  className,
  children,
  hideLabels,
  hideLabel,
  textfieldClassName,
  loginLabel,
  ...props
}: TextInputWrapperProps) => {
  return (
    <div className={clsx("w-full", className)}>
      <TextField
        {...props.field}
        isDisabled={props.field?.disabled}
        isInvalid={!!error}
        className={textfieldClassName}
      >
        <InputHeader
          label={label}
          tooltipText={tooltipText}
          helperText={helperText}
          required={required}
          hideLabels={hideLabel}
          loginLabel={loginLabel}
        />
        {children}
        {error && (
          <Typography
            className={clsx("mt-1 font-labels-prominent-2 text-info-error", hideLabels && "hidden")}
            size="label-5"
            variant="prominent-2"
          >
            <FieldError>{error}</FieldError>
          </Typography>
        )}
      </TextField>
    </div>
  );
};
